(() => {
  const SELECTOR = '[data-infographic-inline]';
  const PROCESSED_STATE = 'ready';
  const FALLBACK_STATE = 'fallback';
  const LOADING_STATE = 'loading';
  const SKIPPED_STATE = 'skipped';
  const HIDDEN_STATE = 'hidden';

  function getViewportWidth() {
    if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth) && window.innerWidth > 0) {
      return window.innerWidth;
    }

    if (typeof document !== 'undefined' && Number.isFinite(document?.documentElement?.clientWidth)) {
      return document.documentElement.clientWidth;
    }

    return null;
  }

  function shouldInlineForViewport(mount) {
    const minWidth = Number(mount.dataset.minWidth);
    const maxWidth = Number(mount.dataset.maxWidth);
    if (!Number.isFinite(minWidth) || minWidth <= 0) {
      if (!Number.isFinite(maxWidth) || maxWidth <= 0) {
        return true;
      }
    }

    const viewportWidth = getViewportWidth();
    if (!Number.isFinite(viewportWidth)) {
      return true;
    }

    if (Number.isFinite(minWidth) && minWidth > 0 && viewportWidth < minWidth) {
      return false;
    }

    if (Number.isFinite(maxWidth) && maxWidth > 0 && viewportWidth > maxWidth) {
      return false;
    }

    return true;
  }

  function resolveResponsiveSource(mount, rawSrc) {
    const src = rawSrc || '';
    if (!src) {
      return '';
    }

    const minWidth = Number(mount.dataset.minWidth);
    const maxWidth = Number(mount.dataset.maxWidth);
    const isMobileOnlyMount = (!Number.isFinite(minWidth) || minWidth <= 0) && Number.isFinite(maxWidth) && maxWidth > 0;

    if (!isMobileOnlyMount || src.includes('-mobile.svg')) {
      return src;
    }

    if (!/\.svg($|[?#])/i.test(src)) {
      return src;
    }

    return src.replace(/\.svg(?=($|[?#]))/i, '-mobile.svg');
  }

  function resolveMountSource(mount) {
    return resolveResponsiveSource(mount, mount.dataset.src || '');
  }

  function resolveFallbackSource(mount) {
    return resolveResponsiveSource(mount, mount.dataset.fallbackSrc || '');
  }

  function syncSectionVisibility(mount) {
    const section = mount.closest('.shopify-section');
    if (!section) {
      return;
    }

    const allMounts = section.querySelectorAll(SELECTOR);
    const hasVisibleMount = Array.from(allMounts).some((item) => {
      const state = item.dataset.state || '';
      return state !== HIDDEN_STATE && state !== SKIPPED_STATE;
    });
    section.hidden = !hasVisibleMount;
  }

  function hideMount(mount) {
    const figure = mount.closest('.ts-infographic__figure');
    if (figure) {
      figure.hidden = true;
    } else {
      mount.hidden = true;
    }
    mount.dataset.state = HIDDEN_STATE;
    syncSectionVisibility(mount);
  }

  function buildFallbackImage(mount, src) {
    const img = document.createElement('img');
    img.className = 'ts-infographic__image';
    img.alt = mount.dataset.alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';

    if (mount.dataset.width) {
      img.width = Number(mount.dataset.width);
    }
    if (mount.dataset.height) {
      img.height = Number(mount.dataset.height);
    }

    return new Promise((resolve, reject) => {
      img.addEventListener(
        'load',
        () => {
          resolve(img);
        },
        { once: true }
      );
      img.addEventListener(
        'error',
        () => {
          reject(new Error(`Failed to load fallback infographic image: ${src}`));
        },
        { once: true }
      );
      img.src = src;
    });
  }

  async function createFallbackImage(mount, src) {
    if (!src) {
      hideMount(mount);
      return;
    }

    try {
      const img = await buildFallbackImage(mount, src);
      mount.replaceChildren(img);
      mount.dataset.state = FALLBACK_STATE;
      syncSectionVisibility(mount);
    } catch (_error) {
      const fallbackSrc = resolveFallbackSource(mount);
      if (fallbackSrc && fallbackSrc !== src) {
        mount.dataset.fallbackSrc = '';
        await createFallbackImage(mount, fallbackSrc);
        return;
      }
      hideMount(mount);
    }
  }

  async function inlineSvg(mount, src = resolveMountSource(mount)) {
    if (!src || mount.dataset.state === PROCESSED_STATE || mount.dataset.state === LOADING_STATE) {
      return;
    }

    if (!shouldInlineForViewport(mount)) {
      mount.replaceChildren();
      mount.dataset.state = SKIPPED_STATE;
      return;
    }

    mount.dataset.state = LOADING_STATE;

    try {
      const response = await fetch(src, {
        credentials: 'omit',
        mode: 'cors',
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status}`);
      }

      const svgText = await response.text();
      if (!/<svg[\s>]/i.test(svgText)) {
        throw new Error('Response did not contain an SVG root.');
      }

      mount.innerHTML = svgText;
      const svg = mount.querySelector('svg');
      if (!svg) {
        throw new Error('Unable to parse inline SVG.');
      }

      svg.classList.add('ts-infographic__image', 'ts-infographic__image--inline');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      if (!svg.hasAttribute('focusable')) {
        svg.setAttribute('focusable', 'false');
      }

      mount.dataset.state = PROCESSED_STATE;
      syncSectionVisibility(mount);
    } catch (error) {
      const fallbackSrc = resolveFallbackSource(mount);
      if (fallbackSrc && fallbackSrc !== src) {
        console.warn('[Timeseek] Failed to inline infographic SVG, retrying fallback asset.', error);
        mount.dataset.fallbackSrc = '';
        await inlineSvg(mount, fallbackSrc);
        return;
      }
      console.warn('[Timeseek] Failed to inline infographic SVG, falling back to <img>.', error);
      await createFallbackImage(mount, src);
    }
  }

  function boot(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return;
    }

    if (root instanceof Element && root.matches?.(SELECTOR)) {
      inlineSvg(root);
    }

    const mounts = root.querySelectorAll(SELECTOR);
    mounts.forEach((mount) => {
      inlineSvg(mount);
    });
  }

  function observeMutations() {
    if (typeof MutationObserver !== 'function') {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches?.(SELECTOR)) {
            inlineSvg(node);
            return;
          }

          boot(node);
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function bindShopifyEvents() {
    document.addEventListener('shopify:section:load', (event) => {
      const target = event?.target instanceof Element ? event.target : document;
      boot(target);
    });
  }

  function bindViewportEvents() {
    if (typeof window?.addEventListener !== 'function') {
      return;
    }

    let rafId = 0;
    const rerender = () => {
      if (rafId && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafId);
      }

      const run = () => {
        rafId = 0;
        boot(document);
      };

      if (typeof window.requestAnimationFrame === 'function') {
        rafId = window.requestAnimationFrame(run);
        return;
      }

      run();
    };

    window.addEventListener('resize', rerender, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot(document);
      observeMutations();
      bindShopifyEvents();
      bindViewportEvents();
    }, { once: true });
  } else {
    boot(document);
    observeMutations();
    bindShopifyEvents();
    bindViewportEvents();
  }
})();
